import { Mob } from './Mob';

export class BossMob extends Mob {
    constructor(scene, x, y, config = {}) {
        // Set default boss-specific configurations
        const bossConfig = {
            enemyType: 'boss',
            health: config.health || 300,
            speed: config.speed || 45,
            damage: config.damage || 25,
            attackRange: config.attackRange || 50,
            detectionRange: config.detectionRange || 200,
            attackCooldown: config.attackCooldown || 2000,
            skillCooldown: config.skillCooldown || 8000,
            shouldPatrol: false, // Bosses typically don't patrol
            ...config
        };

        super(scene, x, y, bossConfig);
        
        // Boss-specific properties
        this.skillRange = config.skillRange || 80;
        this.enrageThreshold = config.enrageThreshold || 0.3; // 30% health
        this.isEnraged = false;
        this.lastSkillTime = 0;
        this.skillChance = config.skillChance || 0.3; // 30% chance to use skill instead of attack
        
        // Phase system
        this.phases = config.phases || ['normal', 'enraged'];
        this.currentPhase = 0;
        
        // Combat tracking
        this.combatStartTime = 0;
        this.inCombat = false;
        this.lastPlayerPosition = { x: 0, y: 0 };
    }

    update(deltaTime) {
        super.update(deltaTime);

        // Check for phase transitions
        this.checkPhaseTransition();
        
        // Update combat state
        this.updateCombatState();
    }

    checkPhaseTransition() {
        const healthPercent = this.getHealthPercentage();
        
        // Transition to enraged phase
        if (!this.isEnraged && healthPercent <= this.enrageThreshold) {
            this.enterEnragedPhase();
        }
    }

    enterEnragedPhase() {
        this.isEnraged = true;
        this.currentPhase = 1;
        
        // Increase combat capabilities
        this.speed *= 1.3;
        this.damage *= 1.2;
        this.attackCooldownTime *= 0.7; // Attack faster
        this.skillCooldownTime *= 0.8; // Use skills more often
        this.skillChance = 0.5; // 50% chance to use skill
        
        // Visual effects
        this.sprite.setTint(0xff4444); // Red tint for enrage
        
        // Emit enrage event
        this.scene.events.emit('bossEnraged', this);
    }

    updateCombatState() {
        const wasInCombat = this.inCombat;
        this.inCombat = this.canSeePlayer();
        
        if (!wasInCombat && this.inCombat) {
            this.combatStartTime = this.scene.time.now;
            this.scene.events.emit('bossCombatStart', this);
        } else if (wasInCombat && !this.inCombat) {
            this.scene.events.emit('bossCombatEnd', this);
        }
    }

    // Override attack decision to include skill usage
    dealDamageToPlayer() {
        const currentTime = this.scene.time.now;
        const timeSinceLastSkill = currentTime - this.lastSkillTime;
        
        // Check if we should use skill instead of regular attack
        if (timeSinceLastSkill >= this.skillCooldownTime && 
            this.isPlayerInSkillRange() && 
            Math.random() < this.skillChance) {
            
            this.lastSkillTime = currentTime;
            this.stateMachine.changeState('SkillState');
        } else {
            // Regular attack
            super.dealDamageToPlayer();
        }
    }

    isPlayerInSkillRange() {
        const distance = this.getDistanceToPlayer();
        return distance <= this.skillRange && !this.isDead;
    }

    executeSkillEffect() {
        // Base boss skill - can be overridden in specific boss types
        // NOTE: Don't play skill sound here anymore - it's now played in SkillState.enter()
        const player = this.getPlayer();
        if (!player) return;

        // Area damage around boss
        const distance = this.getDistanceToPlayer();
        if (distance <= this.skillRange) {
            const skillDamage = Math.round(this.damage * 1.5);
            player.modifyHealth(-skillDamage); // Use negative value to reduce health
            
            // Visual effect
            this.createSkillEffect();
        }
    }

    createSkillEffect() {
        // Create area effect visual
        const effectCircle = this.scene.add.circle(this.x, this.y, this.skillRange, 0xffaa00, 0.4);
        effectCircle.setDepth(15);
        effectCircle.setScale(0.1);
        
        this.scene.tweens.add({
            targets: effectCircle,
            scaleX: 1,
            scaleY: 1,
            alpha: 0,
            duration: 800,
            ease: 'Power2',
            onComplete: () => effectCircle.destroy()
        });
    }

    // More intelligent movement for bosses
    moveTowardsPlayer() {
        const player = this.getPlayer();
        if (!player) return;

        const distance = this.getDistanceToPlayer();
        const direction = this.getDirectionToPlayer();
        this.facingDirection = direction.x > 0 ? 1 : -1;

        // Bosses have more complex movement patterns
        if (this.isEnraged) {
            // In enraged phase, more aggressive movement
            if (distance > this.attackRange * 0.7) {
                this.setVelocity(direction.x * this.speed, direction.y * this.speed);
            } else {
                // Sometimes circle around the player
                if (Math.random() < 0.3) {
                    const circleDirection = Math.random() < 0.5 ? 1 : -1;
                    this.setVelocity(circleDirection * this.speed * 0.8, direction.y * this.speed * 0.5);
                } else {
                    this.setVelocity(0, 0);
                }
            }
        } else {
            // Normal phase movement
            if (distance > this.attackRange) {
                this.setVelocity(direction.x * this.speed * 0.8, direction.y * this.speed * 0.8);
            } else {
                this.setVelocity(0, 0);
            }
        }
    }

    takeDamage(amount, damageDirection = null) {
        // Bosses might have damage reduction
        const reducedDamage = this.isEnraged ? amount : amount * 0.9;
        
        const wasDamaged = super.takeDamage(reducedDamage, damageDirection);
        
        if (wasDamaged) {
            // Emit boss damage event
            this.scene.events.emit('bossTookDamage', {
                boss: this,
                damage: reducedDamage,
                healthPercent: this.getHealthPercentage()
            });
        }
        
        return wasDamaged;
    }

    onDeath() {
        super.onDeath();
        
        // Boss death effects
        this.scene.events.emit('bossDefeated', this);
        
        // Drop special loot
        this.scene.events.emit('mobDropLoot', {
            mobType: this.mobType,
            enemyType: this.enemyType,
            position: { x: this.x, y: this.y },
            lootType: 'boss',
            value: this.maxHealth // Bosses drop valuable loot
        });
    }

    // Boss-specific debug info
    getDebugInfo() {
        const baseInfo = super.getDebugInfo();
        return {
            ...baseInfo,
            phase: this.phases[this.currentPhase],
            isEnraged: this.isEnraged,
            inCombat: this.inCombat,
            timeSinceLastSkill: this.scene.time.now - this.lastSkillTime
        };
    }

    destroy() {
        // Clean up debug rectangle
        if (this.debugRect) {
            this.debugRect.destroy();
            this.debugRect = null;
        }
        
        super.destroy();
    }
}

// Specific boss types
export class SkeletonKnightBoss extends BossMob {
    constructor(scene, x, y, config = {}) {
        super(scene, x, y, {
            mobType: 'skeleton_knight',
            health: 400,
            speed: 50,
            damage: 30,
            attackRange: 55,
            skillRange: 90,
            skillCooldown: 6000,
            enrageThreshold: 0.25, // Enrages at 25% health
            // Larger collision size for 2x scaled boss sprite
            mobCollision: { x: 0, y: 0, width: 24, height: 24 },
            // Audio configuration
            attackSounds: ['skeleton-knight-swing-1', 'skeleton-knight-swing-2'],
            damageSounds: ['skeleton-knight-hurt-1', 'skeleton-knight-hurt-2'],
            deathSounds: ['skeleton-knight-death'],
            skillSounds: ['skeleton-knight-shield-clang'],
            ...config
        });

        this.shieldBashRange = 70;
        this.hasShield = true;
        this.blockChance = 0.2; // 20% chance to block attacks
    }

    createSprite() {
        
        // Create the sprite based on mob type
        this.sprite = this.scene.add.sprite(this.x, this.y, this.mobType);
        this.sprite.setOrigin(0.5, 1); // Bottom center origin
        this.sprite.setDepth(15); // Higher depth for boss
        
        // Set initial frame explicitly (frame 0 for idle)
        this.sprite.setFrame(0);
        
        // Scale up the Skeleton Knight to be more imposing (exactly 2x for pixel perfect scaling)
        this.sprite.setScale(2);
        
        // Ensure sprite is visible and properly positioned
        this.sprite.setVisible(true);
        this.sprite.setAlpha(1);
        
        // Try to start idle animation
        if (this.scene.anims.exists('skeleton_knight_idle')) {
            this.sprite.play('skeleton_knight_idle');
        } else {
            console.warn('skeleton_knight_idle animation does not exist');
        }
        
        // Store reference for easy access
        this.sprite.mobInstance = this;
    }

    executeSkillEffect() {
        // Shield bash - cone attack in front of the boss
        // NOTE: Don't play skill sound here anymore - it's now played in SkillState.enter()
        const player = this.getPlayer();
        if (!player) return;

        const distance = this.getDistanceToPlayer();
        const direction = this.getDirectionToPlayer();
        
        // Check if player is in front of the boss (cone attack)
        const dotProduct = direction.x * this.facingDirection;
        const isInFront = dotProduct > 0.5; // Player must be roughly in front
        
        if (distance <= this.shieldBashRange && isInFront) {
            const bashDamage = Math.round(this.damage * 2);
            player.modifyHealth(-bashDamage); // Use negative value to reduce health
            
            // Create shield bash effect
            this.createShieldBashEffect();
        }
    }

    createShieldBashEffect() {
        // Create cone effect in front of boss (scaled up to match 2x boss size)
        const bashEffect = this.scene.add.graphics();
        bashEffect.fillStyle(0xdddddd, 0.6);
        bashEffect.setDepth(15);
        
        // Draw cone (scaled up for the larger boss)
        bashEffect.beginPath();
        bashEffect.moveTo(this.x, this.y);
        bashEffect.lineTo(
            this.x + this.facingDirection * this.shieldBashRange,
            this.y - 40 // Scaled up from -20 to -40
        );
        bashEffect.lineTo(
            this.x + this.facingDirection * this.shieldBashRange,
            this.y + 40 // Scaled up from +20 to +40
        );
        bashEffect.closePath();
        bashEffect.fillPath();
        
        // Animate effect
        this.scene.tweens.add({
            targets: bashEffect,
            alpha: 0,
            duration: 400,
            onComplete: () => bashEffect.destroy()
        });
    }

    takeDamage(amount, damageDirection = null) {
        // Chance to block damage with shield
        if (this.hasShield && !this.isEnraged && Math.random() < this.blockChance) {
            // Block the attack
            this.createBlockEffect();
            return false;
        }
        
        return super.takeDamage(amount, damageDirection);
    }

    createBlockEffect() {
        // Visual block effect (positioned for 2x scaled boss)
        const blockEffect = this.scene.add.text(this.x, this.y - 80, 'BLOCKED!', {
            fontSize: '16px',
            fill: '#ffffff',
            stroke: '#000000',
            strokeThickness: 2
        });
        blockEffect.setOrigin(0.5);
        blockEffect.setDepth(20);
        
        this.scene.tweens.add({
            targets: blockEffect,
            y: blockEffect.y - 20,
            alpha: 0,
            duration: 1000,
            onComplete: () => blockEffect.destroy()
        });
    }

    enterEnragedPhase() {
        super.enterEnragedPhase();
        
        // Lose shield when enraged
        this.hasShield = false;
        this.blockChance = 0;
        
        // Skill becomes more frequent and powerful
        this.skillRange *= 1.2;
        this.shieldBashRange *= 1.3;
    }
} 